import { z } from 'zod';
import { Department } from '../referential/Department';
import { Region } from '../referential/Region';
import { FindLocalPrescriptionOptions } from '../schema/LocalPrescription/FindLocalPrescriptionOptions';
import {
  LocalPrescription,
  LocalPrescriptionUpdate
} from '../schema/LocalPrescription/LocalPrescription';
import {
  LocalPrescriptionComment,
  LocalPrescriptionCommentToCreate
} from '../schema/LocalPrescription/LocalPrescriptionComment';
import {
  ExportPrescriptionOptions,
  FindPrescriptionOptions
} from '../schema/Prescription/FindPrescriptionOptions';
import {
  Prescription,
  PrescriptionUpdate
} from '../schema/Prescription/Prescription';
import { PrescriptionCounts } from '../schema/Prescription/PrescriptionCounts';
import {
  PrescriptionImportFile,
  PrescriptionImportResult
} from '../schema/Prescription/PrescriptionImport';
import {
  PrescriptionSaveRequest,
  PrescriptionSaveResult
} from '../schema/Prescription/PrescriptionSave';
import type { SubRoutes } from './routes';

export const prescriptionsRoutes = {
  '/prescriptions': {
    params: undefined,
    get: {
      query: FindPrescriptionOptions,
      response: z.array(Prescription),
      permissions: ['readPrescriptions']
    }
  },
  '/prescriptions/counts': {
    params: undefined,
    get: {
      query: FindPrescriptionOptions,
      permissions: ['readPrescriptions'],
      response: PrescriptionCounts
    }
  },
  '/prescriptions/export': {
    params: undefined,
    get: {
      query: ExportPrescriptionOptions,
      permissions: ['readPrescriptions'],
      response: z.custom<Buffer>()
    }
  },
  '/prescriptions/import': {
    params: undefined,
    post: {
      body: PrescriptionImportFile,
      permissions: [
        'updatePrescription',
        'distributePrescriptionToDepartments',
        'distributePrescriptionToSlaughterhouses'
      ],
      response: PrescriptionImportResult
    }
  },
  '/prescriptions/save': {
    params: undefined,
    put: {
      body: PrescriptionSaveRequest,
      permissions: [
        'updatePrescription',
        'distributePrescriptionToDepartments',
        'distributePrescriptionToSlaughterhouses',
        'updatePrescriptionLaboratories'
      ],
      response: PrescriptionSaveResult
    }
  },
  '/prescriptions/regions': {
    params: undefined,
    get: {
      query: FindLocalPrescriptionOptions,
      permissions: ['readPrescriptions'],
      response: z.array(LocalPrescription)
    }
  },
  '/prescriptions/regions/changes-viewed': {
    params: undefined,
    put: {
      body: z.object({
        prescriptionIds: z.array(z.guid()),
        region: Region.nullish(),
        department: Department.nullish()
      }),
      permissions: ['readPrescriptions'],
      response: z.undefined()
    }
  },
  '/prescriptions/:prescriptionId/regions/:region/comments': {
    params: { prescriptionId: z.guid(), region: Region },
    post: {
      body: LocalPrescriptionCommentToCreate,
      permissions: ['commentPrescription'],
      response: LocalPrescriptionComment
    }
  },
  '/prescriptions/:prescriptionId/regions/:region/departments/:department/comments':
    {
      params: {
        prescriptionId: z.guid(),
        region: Region,
        department: Department
      },
      post: {
        body: LocalPrescriptionCommentToCreate,
        permissions: ['commentPrescription'],
        response: LocalPrescriptionComment
      }
    },
  '/prescriptions/:prescriptionId/regions/:region': {
    params: { prescriptionId: z.guid(), region: Region },
    get: {
      query: FindLocalPrescriptionOptions.pick({ includes: true }),
      permissions: ['readPrescriptions'],
      response: LocalPrescription
    },
    put: {
      body: LocalPrescriptionUpdate,
      permissions: ['updatePrescription', 'updatePrescriptionLaboratories'],
      response: LocalPrescription
    }
  },
  '/prescriptions/:prescriptionId/regions/:region/departments/:department': {
    params: {
      prescriptionId: z.guid(),
      region: Region,
      department: Department
    },
    put: {
      body: LocalPrescriptionUpdate,
      permissions: [
        'distributePrescriptionToDepartments',
        'distributePrescriptionToSlaughterhouses',
        'updatePrescriptionLaboratories'
      ],
      response: LocalPrescription
    }
  },
  '/prescriptions/:prescriptionId/regions/:region/departments/:department/companies/:companySiret':
    {
      params: {
        prescriptionId: z.guid(),
        region: Region,
        department: Department,
        companySiret: z.string()
      },
      get: {
        query: FindLocalPrescriptionOptions.pick({ includes: true }),
        permissions: ['readPrescriptions'],
        response: LocalPrescription
      }
    },
  '/prescriptions/:prescriptionId': {
    params: {
      prescriptionId: z.guid()
    },
    put: {
      body: PrescriptionUpdate,
      permissions: ['updatePrescription'],
      response: Prescription
    }
  }
} as const satisfies SubRoutes<'/prescriptions'>;
